import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getLlmStatus, updateSettings } from "../api/admin";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

/**
 * Pill that shows the active LLM (e.g. "Local · qwen2.5:3b" or "API · anthropic").
 * Admins can click to switch between Local (Ollama) and API mode.
 * Non-admins see the same pill but it's read-only.
 */
export default function ModelSwitcher() {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["llm-status"],
    queryFn: async () => (await getLlmStatus()).data.data,
    staleTime: 30_000,
  });

  const switchMut = useMutation({
    mutationFn: (provider) => updateSettings({ llm_provider: provider }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["llm-status"] });
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast("Provider switched", "success");
      setOpen(false);
    },
    onError: () => toast("Could not switch provider", "error"),
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (isLoading || !data) return null;

  const provider = data.provider;
  const label = data.label;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => isAdmin && setOpen((v) => !v)}
        disabled={!isAdmin}
        title={isAdmin ? "Switch LLM provider" : "Active LLM (admin can change)"}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
          isAdmin
            ? "bg-[#2f2f2f] text-dark-text hover:bg-[#383838] cursor-pointer"
            : "bg-[#2a2a2a] text-dark-muted cursor-default"
        }`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${
          provider === "ollama" ? "bg-accent" : "bg-blue-400"
        }`} />
        <span>{label}</span>
        {isAdmin && (
          <ion-icon
            name="chevron-down-outline"
            style={{ fontSize: "11px", opacity: 0.6 }}
          />
        )}
      </button>

      {open && isAdmin && (
        <div className="absolute top-full left-0 mt-2 w-52 rounded-xl bg-[#2a2a2a] py-1.5 shadow-lg ring-1 ring-black/10 z-30">
          <ProviderOption
            active={provider === "ollama"}
            label="Local"
            sub={data.model || "Ollama"}
            dotColor="bg-accent"
            onClick={() => switchMut.mutate("ollama")}
          />
          <ProviderOption
            active={provider === "api"}
            label="API"
            sub={data.api_provider || "Cloud provider"}
            dotColor="bg-blue-400"
            onClick={() => switchMut.mutate("api")}
          />
        </div>
      )}
    </div>
  );
}

function ProviderOption({ active, label, sub, dotColor, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs hover:bg-[#333] transition-colors"
    >
      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-dark-text">{label}</p>
        <p className="text-dark-muted truncate">{sub}</p>
      </div>
      {active && (
        <ion-icon
          name="checkmark-outline"
          style={{ fontSize: "14px", color: "#22c55e" }}
        />
      )}
    </button>
  );
}
